/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  Account,
  AccountAdmin,
  ProximityAreaEnum,
  ProximityRightEnum,
  ProximityUser,
  ActiveStatus,
  ActiveStatusEnum,
  ProximityRole,
  AccountUser,
} from '@models';
import { AccountDbQueryService } from './../services/account-db-query.service';
import { AccountDbSaveService } from './../services/account-db-save.service';
import { UtilFns } from '@utils';
import { EntityError, EntityState } from 'breeze-client';
import { BaseService, Roles, SaveAndQueryComponent, StatusService } from '@core';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountActivationService } from '../services/account-activation.service';
import * as _ from 'lodash';
import { AccountUserFinderDialog } from '../account-users/account-user-finder.dialog';

@Component({
  selector: 'prox-account-administrator-frm',
  templateUrl: './account-administrator-frm.component.html',
  standalone: false,
})
export class AccountAdminFrmComponent extends SaveAndQueryComponent {
  // Expose Roles to the template for the GlobalAdmin-only Training Mode panel
  readonly Roles = Roles;

  accountId?: string;
  account?: Account;
  accountName?: string;
  accountAdminId?: string;
  accountAdmin!: AccountAdmin;
  proximityUser!: ProximityUser;
  allRoles!: ProximityRole[];
  activeStatuses: ActiveStatus[] = [];
  formTitle = '* New Administrator *';
  linkedAccountUser?: AccountUser;

  isBeingAdded!: boolean;
  isValidateActivated = false;
  areas = [ProximityAreaEnum.Accounts];

  // Training Mode unlock preset (transient form state; not persisted)
  trainingUnlockPreset: '1h' | '4h' | 'eod' | 'forever' | 'custom' = '1h';
  trainingUnlockCustom: Date | null = null;

  /** Sentinel "permanently unlocked" date stored in trainingUnlockedUntil. */
  static readonly TRAINING_UNLOCK_FOREVER = new Date('9999-12-31T00:00:00Z');

  readonly trainingUnlockOptions = [
    { label: '1 hour', value: '1h' },
    { label: '4 hours', value: '4h' },
    { label: 'End of business day', value: 'eod' },
    { label: 'Forever (no expiration)', value: 'forever' },
    { label: 'Custom…', value: 'custom' },
  ];

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
    public pngDialogService: DialogService,
    private statusService: StatusService,
    public activationService: AccountActivationService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }
  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    this.accountAdminId = params['accountAdminId'];

    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    UtilFns.assertNonEmptyString(this.accountAdminId, 'accountAdminId');

    [this.account, this.allRoles, this.activeStatuses] = await Promise.all([
      this.dbQueryService.getAccountById(this.accountId),
      this.dbQueryService.getAccountRolesByAccountId(this.accountId),
      this.dbQueryService.getAll(ActiveStatus),
    ]);
    this.allRoles = this.allRoles.filter((x) => x.areaId == ProximityAreaEnum.Accounts);

    UtilFns.assertNonNull(this.account, 'Account');

    this.isBeingAdded = this.accountAdminId === 'add';

    if (this.isBeingAdded) {
      [this.accountAdmin, this.proximityUser] = this.createAccountAdmin();
    } else {
      this.accountAdmin = await this.dbQueryService.getAccountAdminById(this.accountAdminId);
      this.proximityUser = this.accountAdmin?.proximityUser;
      this.formTitle = this.accountAdmin.proximityUser.fullName;
      this.linkedAccountUser = _.first(this.accountAdmin.proximityUser.accountUsers);
    }

    this.isValidateActivated = this.activationService.hasErrors;
    if (this.isValidateActivated) {
      this.activationService.activate(this.account.id);
    }

    UtilFns.assertNonNull(this.authUser);
    this.setTitle('Account Administrator for ' + this.account.name);
    this.isPageReady = true;
  }

  override get entity() {
    return this.accountAdmin;
  }

  private createAccountAdmin() {
    const proximityUser = this.dbSaveService.createEntity(ProximityUser, {
      activeStatusId: ActiveStatusEnum.Active,
    });

    const accountAdmin = this.dbSaveService.createEntity(AccountAdmin, {
      id: proximityUser.id,
      accountId: this.accountId,
      proximityUserId: proximityUser.id,
    });

    const entities = [accountAdmin, proximityUser] as const;
    entities.forEach((e) => {
      e.entityAspect.validateEntity();
      e.entityAspect.setUnchanged();
    });
    return entities;
  }

  statusMessage(): string {
    return <string>this.statusService.getWorkingStatus(this.accountAdmin as any).statusDisplay;
  }

  public isReadOnly() {
    // return this.statusService.getWorkingStatus(this.accountAdmin).isActiveReadOnly;
    // Read the page's own Account, not accountAdmin.account: that navigation goes null whenever the
    // AccountAdmin is detached (a failed save of a new administrator does this), and the template
    // calls this on every change-detection pass.
    return !(this.account?.activeStatusId === ActiveStatusEnum.Hold);
  }

  isPrimaryAdmin(): boolean {
    return this.accountAdmin.id === this.account?.primaryAccountAdminId;
  }

  getAccountUserEditLabel() {
    const label = this.linkedAccountUser == null ? 'Create Linked Account User' : 'Edit Account User';
    return label;
  }

  async editAsUser() {
    if (this.isBeingAdded) return;
    if (this.dbSaveService.hasChanges()) {
      this.dialogService.okDialog('Please Save Changes first', 'You must save changes before editing as user.');
      return;
    }
    const proximityUser = this.accountAdmin.proximityUser;
    let accountUser = proximityUser.accountUsers[0];
    if (accountUser == null) {
      accountUser = this.dbSaveService.createEntity(AccountUser, {
        id: proximityUser.id,
        accountId: this.accountId,
        proximityUserId: proximityUser.id,
      });
      await this.dbSaveService.saveSelectedChanges([accountUser]);
    }

    this.router.navigate(['account/accounts', this.accountId, 'manage', 'account-users', accountUser.id]);
    // path: 'account-users/:accountUserId',
  }

  async linkUser() {
    UtilFns.assert(this.accountId != null, 'accountId is null');
    const allAccountUsers = await this.dbQueryService.getAccountUsers(this.accountId);
    const items = await this.dialogService.createFinder(AccountUserFinderDialog, { accountUsers: allAccountUsers });
    if (!items?.length) {
      return;
    }
    const linkedAccountUser = items[0];
    const ok = await this.dialogService.askIfOk(
      'Linking Account User',
      `This will link the Account User "${linkedAccountUser.proximityUser.fullName}" to this Administrator. </br>
       This means that user "${linkedAccountUser.proximityUser.fullName}"'s  info will all be replaced by the Administrator info from "${this.accountAdmin.proximityUser.fullName}".</br>
       This is not reversible.  Are you sure?`,
    );
    if (!ok) {
      return;
    }

    linkedAccountUser.proximityUserId = this.proximityUser.id;
    await this.dbSaveService.saveSelectedChanges([linkedAccountUser]);
    this.linkedAccountUser = linkedAccountUser;
  }

  /** Enables the "Change..." link on the (disabled) email field — see UserFrmComponent. */
  get canChangeEmail(): boolean {
    if (this.isBeingAdded) return false;
    if (this.hasChanges()) return false;
    return !!(this.authUser?.isGlobalAdmin || this.authUser?.hasRole(Roles.PrincipalAccountAdmin));
  }

  // --------------- Save & Undo -------------------------------

  override async addCrossValidationErrors() {
    // Authoritative check via Identity, not Breeze: catches orphan AspNetUsers rows that
    // have no matching ProximityUser (see Amanda Bortz 2026-05-25 repro).
    // Only check on add or when the username actually changed; on edit, the user's own
    // existing row would otherwise be flagged as a collision against itself.
    const usernameWasChanged = this.isBeingAdded
      || 'username' in this.proximityUser.entityAspect.originalValues;
    if (usernameWasChanged) {
      const ok = await this.baseService.authService.isUsernameAvailable(this.proximityUser.username);
      if (!ok) {
        this.createValidationError(this.proximityUser, 'username', 'This Login Name has already been taken.');
      }
    }

    // Email uniqueness is enforced by a unique EmailIndex on AspNetUsers (migration
    // 20260525113834_UniqueEmailIndex), so a Breeze-only check would miss orphan rows
    // (see project_email-dup-cleanup-and-migration-bug). Edit-mode collisions are not
    // possible here because the email field is disabled on edit — changes only go
    // through the dedicated "Change E-Mail..." link, which has its own conflict flow.
    if (this.isBeingAdded && this.proximityUser.email != null) {
      const emailOk = await this.baseService.authService.isEmailAvailable(this.proximityUser.email, this.proximityUser.id);
      if (!emailOk) {
        this.createValidationError(this.proximityUser, 'email', 'This email is already in use by another user.');
      }
    }

    if (this.proximityUser.activeStatusId !== ActiveStatusEnum.Active && this.isPrimaryAdmin()) {
      this.createValidationError(
        this.proximityUser,
        'username',
        "Account Primary Administrator must have Status = 'Active'.  To deactivate this Administrator, reassign a different Primary Administrator.",
      );
    }

    // Optional chaining matters: on a NEW administrator the Role dropdown starts empty, so
    // proximityRole is null and reading .name threw before Breeze could report the missing role.
    // isPrimaryAdmin() a few lines down already guards the same access this way.
    // undefined != PrincipalAccountAdmin is true, so a primary admin with no role still errors —
    // the check keeps its meaning, it just no longer crashes first.
    if (this.proximityUser.proximityRole?.name != Roles.PrincipalAccountAdmin && this.isPrimaryAdmin()) {
      this.createValidationError(
        this.proximityUser,
        'username',
        'Account Principal Administrator must have PrincipalAdministrator privileges.',
      );
    }
  }

  override async beforeSave() {
    if (this.isBeingAdded) {
      [this.accountAdmin, this.proximityUser].forEach((e) => {
        e.entityAspect.setAdded();
      });
    }
    this.formTitle = this.accountAdmin.proximityUser.fullName;
    return true;
  }

  override async afterSave() {
    // no longer an added entity after a save.
    this.isBeingAdded = false;

    if (this.isValidateActivated) {
      if (!(await this.activationService.activate(this.account!.id))) {
        this.account!.activeStatusId = ActiveStatusEnum.Active;
        this.activationService.deactivate();
        this.isValidateActivated = false;
        await this.saveChanges();
        await this.dialogService.okDialog('Activation Status', "Account is successfully updated to 'Active'");
      }
    }

    // if saving changes to yourself, reload the page so AuthUser gets the changes
    if (this.proximityUser.id == this.authUser?.id || this.proximityUser.id == this.authUser?.actingAsUser?.id) {
      location.reload();
    }
  }

  override handleSaveError(err: Error, errMessage: string) {
    // A failed save rolls back every pending change, and rolling back an Added entity DETACHES it.
    // For a new administrator that strands the form on two detached entities: everything typed is
    // still on them, but they are no longer in the cache, so Save has nothing to send and
    // accountAdmin.account is null. Re-attach them as Added so the operator can correct the problem
    // and save again. An Undo then detaches them, and afterUndo() builds a fresh blank pair.
    if (this.isBeingAdded) {
      [this.proximityUser, this.accountAdmin].forEach((e) => {
        if (e?.entityAspect.entityState.isDetached()) {
          this.dbSaveService.uow.attachEntity(e, EntityState.Added);
        }
      });
    }
    super.handleSaveError(err, errMessage);
  }

  override async afterUndo() {
    if (this.isBeingAdded) {
      // previous entities were detached by the undo
      [this.accountAdmin, this.proximityUser] = this.createAccountAdmin();
    }
  }

  override navigateToValidationError(error: EntityError) {
    const errEnt = error.entity;
    const prop = error.propertyName;
    UtilFns.focusInputByEntity('#topLevel', errEnt, prop);
  }

  // --------------- Training Mode (GlobalAdmin only) -------------------------------

  /** True when the Training Mode panel should be shown: editing a PAA, viewer is GlobalAdmin. */
  get canManageTrainingMode(): boolean {
    if (this.isBeingAdded) return false;
    if (!this.authUser?.hasRole(Roles.GlobalAdmin)) return false;
    return this.proximityUser?.proximityRole?.name === Roles.PrincipalAccountAdmin;
  }

  /** True when the PAA's unlock window is currently in effect. */
  get isCurrentlyUnlocked(): boolean {
    const until = this.accountAdmin?.trainingUnlockedUntil;
    if (!until) return false;
    return new Date(until).getTime() > Date.now();
  }

  /** True when the unlock is the "forever" sentinel (year 9999). */
  get isPermanentlyUnlocked(): boolean {
    const until = this.accountAdmin?.trainingUnlockedUntil;
    if (!until) return false;
    return new Date(until).getUTCFullYear() >= 9999;
  }

  /** Apply the chosen preset to accountAdmin.trainingUnlockedUntil. User must click Save to persist. */
  applyTrainingUnlock() {
    let target: Date | null = null;
    const now = new Date();
    switch (this.trainingUnlockPreset) {
      case '1h':
        target = new Date(now.getTime() + 60 * 60 * 1000);
        break;
      case '4h':
        target = new Date(now.getTime() + 4 * 60 * 60 * 1000);
        break;
      case 'eod': {
        // Local 5:00 PM today; if it's already past 5 PM, use 5 PM tomorrow.
        target = new Date(now);
        target.setHours(17, 0, 0, 0);
        if (target.getTime() <= now.getTime()) {
          target.setDate(target.getDate() + 1);
        }
        break;
      }
      case 'forever':
        target = AccountAdminFrmComponent.TRAINING_UNLOCK_FOREVER;
        break;
      case 'custom':
        if (!this.trainingUnlockCustom) {
          this.dialogService.okDialog('Pick a date/time', 'Please choose a custom unlock date and time first.');
          return;
        }
        if (new Date(this.trainingUnlockCustom).getTime() <= now.getTime()) {
          this.dialogService.okDialog('Invalid time', 'Custom unlock time must be in the future.');
          return;
        }
        target = new Date(this.trainingUnlockCustom);
        break;
    }
    this.accountAdmin.trainingUnlockedUntil = target!;
  }

  /** Immediately re-lock by clearing trainingUnlockedUntil. User must click Save to persist. */
  lockTrainingNow() {
    // Cast through unknown: the generated model types it Date? but Breeze accepts null to clear.
    this.accountAdmin.trainingUnlockedUntil = null as unknown as Date;
  }
}
